<script setup lang="ts">
import UserHeader from "@/Pages/Users/Partials/UserHeader.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppFormInputText from "@/Components/Shared/forms/AppFormInputText.vue";
import AppPagination from "@/Components/Shared/pagination/AppPagination.vue";
import AppPlaceholder from "@/Components/Shared/placeholder/AppPlaceholder.vue";
import AppReviewGrid from "@/Components/App/reviews/AppReviewGrid.vue";
import { reactive, watch, computed, ref } from "vue";
import { pickBy } from "lodash-es";
import { router } from "@inertiajs/vue3";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppGrid from "@/Components/Shared/grid/AppGrid.vue";
import AppColumn from "@/Components/Shared/grid/AppColumn.vue";
import AppFormInputCheckbox from "@/Components/Shared/forms/AppFormInputCheckbox.vue";
import AppText from "@/Components/Shared/text/AppText.vue";
import AppFormGroup from "@/Components/Shared/forms/AppFormGroup.vue";
import AppButton from "@/Components/Shared/button/AppButton.vue";
import AppUserList from "@/Components/App/users/AppUserList.vue";
import AppIcon from "@/Components/Shared/icon/AppIcon.vue";

interface Filter {
    categories?: string[];
    themes?: string[];
    search?: string;
}

interface Theme {
    data: any[];
}

type Props = {
    user: {
        data: {
            path: string;
            username: string;
            name: string;
            reviews_count: number;
        };
    };
    relatedUsers?: object;
    reviews: {
        data: any[];
        meta: {
            links: any[];
            last_page: number;
        };
    };
    themes?: Theme;
    userCategories?: {
        data: any[];
    };
    filters?: Filter;
    filteredCount?: number;
};

const {
    user,
    reviews,
    themes = undefined,
    userCategories = undefined,
    filters = undefined,
    relatedUsers = undefined,
    filteredCount = undefined,
} = defineProps<Props>();

const form = reactive({
    categories: filters && filters.categories ? filters.categories : [],
    themes: filters && filters.themes ? filters.themes : [],
    search: filters && filters.search ? filters.search : "",
});

// Mobile filter toggle state
const showMobileFilters = ref(false);

// Compute which themes are selected
const selectedThemes = computed(() => {
    if (!themes || !themes.data) return [];
    return themes.data.filter(theme => form.themes.includes(theme.id));
});

// Get categories for a specific theme
const getCategoriesForTheme = computed(() => {
    return (themeId: string | number) => {
        if (!userCategories || !userCategories.data) return [];
        return userCategories.data.filter(
            category => category.theme && category.theme.id === themeId,
        );
    };
});

// watchers
watch(
    form,
    () => {
        const query = pickBy(form);

        router.get(
            `${user.data.path}/reviews`,
            Object.keys(query).length ? query : { remember: "forget" },
            { preserveState: true, preserveScroll: true },
        );
    },
    { deep: true },
);

// Compute the count to display based on whether filters are active
const displayCount = computed(() => {
    const hasActiveFilters =
        form.search || form.categories.length > 0 || form.themes.length > 0;
    return hasActiveFilters && filteredCount !== undefined
        ? filteredCount
        : user.data.reviews_count;
});

const breadcrumbs = [
    {
        label: "Community",
        path: "/community",
    },
    {
        label: `@${user.data.username}`,
        path: user.data.path,
    },
];
</script>

<template>
    <TheLayoutApp
        :title="
            user.data.name
                ? `Reviews van ${user.data.name} (@${user.data.username})`
                : `Reviews van @${user.data.username}`
        "
        :breadcrumbs="breadcrumbs"
    >
        <AppPage size="small">
            <AppContainer>
                <UserHeader
                    small
                    :user="user.data"
                />
            </AppContainer>
            <AppContainer>
                <!-- Mobile filter button -->
                <div class="md:hidden mb-4">
                    <AppButton
                        variant="secondary"
                        class="w-full"
                        @click="showMobileFilters = !showMobileFilters"
                    >
                        <AppIcon
                            name="list"
                            class="mr-2"
                        />
                        {{
                            showMobileFilters
                                ? "Verberg filters"
                                : "Toon filters"
                        }}
                    </AppButton>
                </div>

                <AppGrid>
                    <AppColumn
                        class="md:col-span-3"
                        :class="{ 'hidden md:block': !showMobileFilters }"
                    >
                        <!-- filters here -->
                        <div
                            class="bg-white rounded-lg p-4 md:p-0 mb-4 md:mb-0 border md:border-0"
                        >
                            <AppHeader
                                variant="between"
                                class="mb-4"
                            >
                                <AppTitle variant="main">Filters</AppTitle>
                                <AppButton
                                    color="ghost"
                                    size="small"
                                    class="md:hidden"
                                    @click="showMobileFilters = false"
                                >
                                    <AppIcon name="cross" />
                                </AppButton>
                            </AppHeader>

                            <div class="space-y-4">
                                <div>
                                    <AppTitle
                                        class="mb-2"
                                        variant="subtitle"
                                    >
                                        Thema's
                                    </AppTitle>
                                    <AppFormGroup>
                                        <div
                                            v-if="
                                                themes &&
                                                themes.data &&
                                                themes.data.length
                                            "
                                            class="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-sm p-3"
                                        >
                                            <template
                                                v-for="theme in themes.data"
                                                :key="theme.id"
                                            >
                                                <AppFormInputCheckbox
                                                    v-model="form.themes"
                                                    :value="theme.id"
                                                    :label="theme.name"
                                                    :name="`theme-${theme.id}`"
                                                />

                                                <!-- Categories per selected theme -->
                                                <div
                                                    v-if="
                                                        form.themes.includes(
                                                            theme.id,
                                                        )
                                                    "
                                                    class="mt-2"
                                                >
                                                    <AppFormGroup>
                                                        <div
                                                            class="space-y-2 pl-4 border-l-2 border-gray-200"
                                                        >
                                                            <div
                                                                v-if="
                                                                    getCategoriesForTheme(
                                                                        theme.id,
                                                                    ).length
                                                                "
                                                                class="space-y-2 py-2"
                                                            >
                                                                <AppFormInputCheckbox
                                                                    v-for="category in getCategoriesForTheme(
                                                                        theme.id,
                                                                    )"
                                                                    :key="
                                                                        category.id
                                                                    "
                                                                    v-model="
                                                                        form.categories
                                                                    "
                                                                    :value="
                                                                        category.id
                                                                    "
                                                                    :label="
                                                                        category.name
                                                                    "
                                                                    :name="`category-${category.id}`"
                                                                />
                                                            </div>
                                                            <AppText
                                                                v-else
                                                                size="small"
                                                                color="gray"
                                                            >
                                                                Geen categorieën
                                                                gevonden binnen
                                                                dit thema
                                                            </AppText>
                                                        </div>
                                                    </AppFormGroup>
                                                </div>
                                            </template>
                                        </div>
                                        <AppText
                                            v-else
                                            size="small"
                                            color="gray"
                                            >Geen thema's beschikbaar</AppText
                                        >
                                    </AppFormGroup>
                                </div>
                            </div>
                        </div>
                    </AppColumn>
                    <AppColumn class="md:col-span-9">
                        <section>
                            <AppHeader variant="between">
                                <AppTitle variant="main"
                                    >Reviews ({{ displayCount }})
                                </AppTitle>
                            </AppHeader>

                            <AppFormInputText
                                v-model="form.search"
                                name="search"
                                placeholder="Zoeken..."
                                class="mb-4"
                            />

                            <AppReviewGrid
                                v-if="
                                    reviews &&
                                    reviews.data &&
                                    reviews.data.length
                                "
                                :reviews="reviews.data"
                                context="user"
                            />
                            <AppPlaceholder v-else>
                                Geen reviews gevonden</AppPlaceholder
                            >

                            <AppPagination
                                v-if="
                                    reviews.meta && reviews.meta.last_page > 1
                                "
                                class="mt-12"
                                :links="reviews.meta.links"
                            />
                        </section>
                    </AppColumn>
                </AppGrid>
            </AppContainer>
        </AppPage>
        <template #sidebar>
            <section v-if="relatedUsers && relatedUsers.data.length">
                <AppHeader>
                    <AppTitle
                        as="h3"
                        variant="subtitle"
                    >
                        Gerelateerde leden
                    </AppTitle>
                </AppHeader>

                <AppUserList :users="relatedUsers.data" />
            </section>
        </template>
    </TheLayoutApp>
</template>
